import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {usePage} from "@inertiajs/react";
import axios from "axios";
import {Alert, Button, Container, Form} from "react-bootstrap";
import {createColumnHelper} from "@tanstack/react-table";
import {mdiInformationOutline} from "@mdi/js";
import DataGrid from "../../Components/DataGrid/DataGrid.jsx";
import HelpTooltip from "../../Components/HelpTooltip.jsx";
import StatusTimestampControl from "../../Components/StatusTimestampControl.jsx";
import ArticleHistoryModal from "../../Components/ArticleHistoryModal.jsx";
import ArticleShopLinks from "../../Components/ArticleShopLinks.jsx";
import ReservationModal from "../../Components/Stock/ReservationModal.jsx";

const columnHelper = createColumnHelper();

// Die technischen Werte bleiben "in"/"out". "In Zulauf" ist die verbindliche
// Bezeichnung für den Zustand, der früher "nicht auf Lager" hieß - sie gilt
// gleichlautend im Änderungslog und im täglichen Bestandsbericht per E-Mail.
const SOLL_STATUS_CONFIG = {
    in: {label: "Auf Lager", variant: "success"},
    out: {label: "In Zulauf", variant: "warning"},
    no_stock: {label: "ohne Bestand", variant: "secondary"},
};

const SHOP_STATUS_CONFIG = {
    in: {label: "Auf Lager", variant: "success"},
    out: {label: "In Zulauf", variant: "warning"},
};

const SHOP_STATUS_FILTER_OPTIONS = [
    {value: "", label: "Alle"},
    {value: "in", label: "Auf Lager"},
    {value: "out", label: "In Zulauf"},
    {value: "__none__", label: "– (unbekannt)"},
];

const SHOP_ACTIVE_CONFIG = {
    active: {label: "Aktiviert", variant: "primary", marker: "●"},
    inactive: {label: "Deaktiviert", variant: "secondary", marker: "○"},
    xxx_disabled: {label: "Durch XXX deaktiviert", variant: "dark", marker: "×"},
};

const DEFAULT_ADDITIONAL_FILTERS = {only_shop_articles: true, shop_scope: ""};

const B2C_SHOP_ACTIVE_FILTER_OPTIONS = [
    {value: "", label: "Alle"},
    {value: "active", label: "Aktiviert"},
    {value: "inactive", label: "Deaktiviert"},
    {value: "__none__", label: "– (unbekannt)"},
];

const B2B_SHOP_ACTIVE_FILTER_OPTIONS = [
    {value: "", label: "Alle"},
    {value: "active", label: "Aktiviert"},
    {value: "inactive", label: "Deaktiviert"},
    {value: "xxx_disabled", label: "Durch XXX deaktiviert"},
    {value: "__none__", label: "– (unbekannt)"},
];

function renderShopStatusBadge(value) {
    if (!value) {
        return <span className="text-muted">–</span>;
    }
    const cfg = SHOP_STATUS_CONFIG[value] || {label: value, variant: "light"};
    return <span className={`badge bg-${cfg.variant}`}>{cfg.label}</span>;
}

function renderShopActiveBadge(value) {
    const cfg = SHOP_ACTIVE_CONFIG[value];
    if (!cfg) {
        return <span className="text-muted">–</span>;
    }
    return <span className={`badge rounded-pill bg-${cfg.variant} d-inline-flex align-items-center gap-1`}>
        <span aria-hidden="true">{cfg.marker}</span>{cfg.label}
    </span>;
}

function formatQuantity(value) {
    if (value === null || value === undefined) {
        return "–";
    }
    return new Intl.NumberFormat("de-DE").format(value);
}

function formatTimestamp(value) {
    if (!value) {
        return "–";
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return "–";
    }
    return date.toLocaleString("de-DE", {
        year: "numeric",
        month: "numeric",
        day: "numeric",
        hour: "numeric",
        minute: "numeric",
    });
}

function formatPrice(value, currency) {
    if (value === null || value === undefined) return "–";
    return new Intl.NumberFormat("de-DE", {minimumFractionDigits: 2, maximumFractionDigits: 2}).format(value) + (currency ? ` ${currency}` : "");
}

/**
 * Liest Vorfilter aus der URL, damit z. B. der Link von der
 * B2B-Lagerverfügbarkeit ("?ueberwacht=1") die Tabelle direkt gefiltert öffnet.
 */
function parseInitialFilters() {
    if (typeof window === "undefined") {
        return [];
    }

    const params = new URLSearchParams(window.location.search);
    const filters = [];

    if (params.get("ueberwacht") === "1") {
        filters.push({id: "ueberwacht", value: "ja"});
    }

    const sollStatus = params.get("soll_status");
    if (sollStatus) {
        filters.push({id: "soll_status", value: sollStatus});
    }

    return filters;
}

/**
 * Die Navigation soll immer mit der Arbeitsansicht „Lager“ starten. Sobald
 * die URL aber einen Zustand trägt (Ansicht oder Vorfilter), ist sie die
 * Quelle der Wahrheit: ein Reload darf diesen Zustand nicht überschreiben.
 */
function initialViewId(views) {
    const lager = views.find((view) => view.slug === "lager");
    if (typeof window === "undefined") return lager?.id ?? null;

    const params = new URLSearchParams(window.location.search);
    const requestedSlug = params.get("view");
    if (requestedSlug) return views.find((view) => view.slug === requestedSlug)?.id ?? null;

    return params.size === 0 ? lager?.id ?? null : null;
}

export default function Overview() {
    const {
        threshold,
        warehouse_labels: warehouseLabels,
        stock_data_warning: stockDataWarning,
        warengruppen,
        price_columns: priceColumns = [],
        advanced_search_fields: advancedSearchFields = [],
        saved_searches: savedSearches = [],
        views: suppliedViews = [],
        user_roles: userRoles = [],
        weclappBaseUrl = "",
    } = usePage().props;

    // Die eigentlichen Artikel-Zeilen lädt DataGrid serverseitig selbst nach
    // (Seite, Filter, Sortierung). Für den Live-Ersatz einer einzelnen Zeile
    // nach "Jetzt aktualisieren" (StatusTimestampControl) greift diese Seite
    // per Ref auf DataGrids interne Zeilen-Verwaltung durch.
    const gridRef = useRef(null);
    const viewWasDraggedRef = useRef(false);

    const handleArticleRefreshed = useCallback((updatedArticle) => {
        if (!updatedArticle?.artikelnummer) {
            return;
        }
        gridRef.current?.replaceRow(updatedArticle.artikelnummer, updatedArticle);
    }, []);

    // EIN gemeinsames Verlaufsfenster für alle Zeilen (statt eines Modals je
    // Zeile) - geöffnet wird es über das Uhrensymbol in der
    // Artikelnummer-Spalte, das den Artikel dieser Zeile setzt.
    const [historyArtikelnummer, setHistoryArtikelnummer] = useState(null);
    const [reservationArtikelnummer, setReservationArtikelnummer] = useState(null);
    const [views, setViews] = useState(suppliedViews);
    const [activeViewId, setActiveViewId] = useState(() => initialViewId(suppliedViews));
    const [gridState, setGridState] = useState(null);
    const [viewName, setViewName] = useState("");
    const [editMode, setEditMode] = useState(false);
    const [draftViewIds, setDraftViewIds] = useState([]);
    const [priceComparison, setPriceComparison] = useState(null);
    const [stockWarning, setStockWarning] = useState(stockDataWarning);
    const [refreshingStock, setRefreshingStock] = useState(false);
    const [stockRefreshError, setStockRefreshError] = useState(null);
    const isAdmin = userRoles.includes("admin");

    const initialColumnFilters = useMemo(() => parseInitialFilters(), []);

    const refreshStockData = async () => {
        setRefreshingStock(true);
        setStockRefreshError(null);
        try {
            const {data} = await axios.post("/stock/overview/refresh-stock-data");
            setStockWarning(data?.stale ? {last_successful_at: data.last_successful_at, stale: true} : null);
            gridRef.current?.applyState(gridRef.current?.getState());
        } catch (error) {
            setStockRefreshError(error.response?.data?.message || "Bestandsabruf fehlgeschlagen.");
        } finally {
            setRefreshingStock(false);
        }
    };

    const columns = useMemo(
        () => [
            columnHelper.accessor("hex_color", {
                id: "hex_color",
                header: "Farbe",
                enableSorting: false,
                enableColumnFilter: false,
                meta: {
                    width: 64,
                    help: "Farbmuster des Artikels. HEX-Farbwert aus dem B2C-Shop; bei Effektfarben (Metallic/Verlauf) stattdessen ein Vorschaubild. Leer bei Artikeln ohne hinterlegte Farbe (z. B. Zubehör, Sets).",
                },
                cell: (info) => {
                    const hexColor = info.getValue();
                    const colorImageUrl = info.row.original.color_image_url;

                    if (hexColor) {
                        return (
                            <span className="d-inline-flex flex-column align-items-start" title={hexColor}>
                                <span
                                    className="d-inline-block rounded-1"
                                    style={{
                                        width: "48px",
                                        height: "24px",
                                        maxWidth: "100%",
                                        border: "1px solid rgba(0,0,0,.15)",
                                        backgroundColor: hexColor,
                                    }}
                                />
                                {/* Der Farbwert direkt unter dem Muster - klein
                                    gehalten, damit die Spalte schmal bleibt. */}
                                <span
                                    className="text-muted font-monospace"
                                    style={{fontSize: "0.65rem", lineHeight: 1.3, letterSpacing: "-0.01em"}}
                                >
                                    {hexColor.toUpperCase()}
                                </span>
                            </span>
                        );
                    }

                    if (colorImageUrl) {
                        return (
                            <span
                                className="d-inline-block rounded-1"
                                style={{
                                    width: "48px",
                                    height: "24px",
                                    maxWidth: "100%",
                                    border: "1px solid rgba(0,0,0,.15)",
                                    backgroundImage: `url(${colorImageUrl})`,
                                    backgroundSize: "cover",
                                    backgroundPosition: "center",
                                }}
                            />
                        );
                    }

                    return null;
                },
            }),
            columnHelper.accessor("artikelnummer", {
                id: "artikelnummer",
                header: "Artikelnummer",
                cell: (info) => (
                    <ArticleShopLinks
                        sku={info.getValue()}
                        b2cDeId={info.row.original.b2c_de_id}
                        b2bStatus={info.row.original.b2b_status}
                        weclappId={info.row.original.weclapp_id}
                        onShowHistory={setHistoryArtikelnummer}
                    />
                ),
                meta: {
                    width: 170,
                    filterVariant: "text",
                    help: "Die Artikelnummer (SKU, SNV-/Schneider-Nummer). Sie verknüpft Weclapp, den B2B- und den B2C-Shop. Darunter Schaltflächen, in welchem System der Artikel geöffnet werden kann (B2C, B2B, Weclapp), sowie ein Uhrensymbol für den Änderungsverlauf dieses Artikels.",
                },
            }),
            columnHelper.accessor("bezeichnung", {
                id: "bezeichnung",
                header: "Bezeichnung",
                cell: (info) => info.getValue() || "–",
                meta: {
                    width: 280,
                    filterVariant: "text",
                    help: "Artikelname aus Weclapp.",
                },
            }),
            columnHelper.accessor("warengruppe", {
                id: "warengruppe",
                header: "Warengruppe",
                cell: (info) => info.getValue() || "–",
                meta: {
                    width: 190,
                    pickerLabel: "Warengruppe",
                    filterVariant: "select",
                    selectOptions: [
                        {value: "", label: "Alle"},
                        ...(warengruppen || []).map((w) => ({value: w, label: w})),
                    ],
                    help: "Produktkategorie/-serie aus Weclapp (z. B. „PREMIUM 400ml Spray\"). Über den Filter-Schalter oben lässt sich die Tabelle nach dieser Spalte gruppieren.",
                },
            }),
            columnHelper.accessor("snv", {
                id: "snv",
                header: warehouseLabels?.["8034"] || "SNV Weilheim",
                cell: (info) => formatQuantity(info.getValue()),
                meta: {
                    width: 130,
                    pickerLabel: "SNV Weilheim",
                    filterVariant: "number",
                    help: "Aktueller Lagerbestand (Stückzahl) im Weclapp-Lager SNV Weilheim. Dieser Wert ist die Grundlage für den Soll-Status.",
                },
            }),
            columnHelper.accessor("snv_zero_since", {
                id: "snv_zero_since",
                header: "SNV Bestand 0 seit",
                cell: (info) => info.row.original.snv === 0 ? formatTimestamp(info.getValue()) : "–",
                meta: {
                    width: 145,
                    pickerLabel: "SNV Bestand 0 seit",
                    help: "Seit diesem Zeitpunkt meldet der Snapshot für SNV Weilheim 0 Stück. Leer bedeutet nicht automatisch ausverkauft: Für diesen Artikel kann dort noch nie ein Bestandssatz geführt worden sein.",
                },
            }),
            columnHelper.accessor("lahr", {
                id: "lahr",
                header: warehouseLabels?.["3827"] || "Lahr Hauptlager",
                cell: (info) => formatQuantity(info.getValue()),
                meta: {
                    width: 140,
                    pickerLabel: "Lahr Hauptlager",
                    filterVariant: "number",
                    help: "Aktueller Lagerbestand (Stückzahl) im Weclapp-Lager Lahr Hauptlager. Nur zur Information – nicht Grundlage des automatischen B2B-Abgleichs.",
                },
            }),
            columnHelper.accessor("lahr_zero_since", {
                id: "lahr_zero_since",
                header: "Lahr Bestand 0 seit",
                cell: (info) => info.row.original.lahr === 0 ? formatTimestamp(info.getValue()) : "–",
                meta: {
                    width: 145,
                    pickerLabel: "Lahr Bestand 0 seit",
                    help: "Seit diesem Zeitpunkt meldet der Snapshot für Lahr Hauptlager 0 Stück.",
                },
            }),
            columnHelper.accessor("ueberwacht", {
                id: "ueberwacht",
                header: "Für B2B-Shop überwacht",
                cell: (info) => (
                    <span className={`badge ${info.getValue() ? "bg-info" : "bg-light text-dark"}`}>
                        {info.getValue() ? "Ja" : "Nein"}
                    </span>
                ),
                meta: {
                    width: 120,
                    pickerLabel: "Für B2B-Shop überwacht",
                    filterVariant: "select",
                    selectOptions: [
                        {value: "", label: "Alle"},
                        {value: "ja", label: "Ja"},
                        {value: "nein", label: "Nein"},
                    ],
                    help: "Ob dieser Artikel Teil der automatischen B2B-Bestandsaktualisierung ist (Ja = in der verwalteten Liste). Nur überwachte Artikel werden automatisch auf „Auf Lager“ bzw. „In Zulauf“ geschaltet.",
                },
            }),
            columnHelper.accessor("b2c_active", {
                id: "b2c_active",
                header: "B2C-Shop-Status",
                cell: (info) => renderShopActiveBadge(info.getValue()),
                meta: {
                    width: 150,
                    pickerLabel: "Aktivierung",
                    filterVariant: "select",
                    selectOptions: B2C_SHOP_ACTIVE_FILTER_OPTIONS,
                    help: <>
                        <div><strong>Aktiviert:</strong> im B2C-Shop vorhanden und freigeschaltet.</div>
                        <div><strong>Deaktiviert:</strong> im B2C-Shop vorhanden, aber abgeschaltet.</div>
                        <div><strong>–:</strong> im B2C-Shop nicht gefunden.</div>
                        <div className="mt-1">Im B2C-Shop gibt es keinen CSV-Import und kein Präfix. Maßgeblich ist die deutsche Sprachfassung; die Nachbarspalte zeigt die Lagerverfügbarkeit.</div>
                    </>,
                },
            }),
            columnHelper.accessor("b2c_status", {
                id: "b2c_status",
                header: "B2C-Shop-Lagerstatus",
                cell: (info) => (
                    <span className="d-inline-flex align-items-center">
                        {renderShopStatusBadge(info.getValue())}
                        <StatusTimestampControl
                            timestamp={info.row.original.b2c_status_synced_at}
                            label="Stand des B2C-Shops"
                            artikelnummer={info.row.original.artikelnummer}
                            onRefreshed={handleArticleRefreshed}
                        />
                    </span>
                ),
                meta: {
                    width: 160,
                    pickerLabel: "Verfügbarkeit",
                    filterVariant: "select",
                    selectOptions: SHOP_STATUS_FILTER_OPTIONS,
                    help: "So ist der Artikel aktuell im B2C-Onlineshop (shop.molotow.com) eingestellt: Auf Lager oder In Zulauf. „–“ = im B2C-Shop nicht gefunden. Das beschreibt die Lagerverfügbarkeit, nicht die Freischaltung in der Nachbarspalte und nicht den berechneten Soll-Status.",
                },
            }),
            columnHelper.accessor("b2b_active", {
                id: "b2b_active",
                header: "B2B-Shop-Status",
                cell: (info) => renderShopActiveBadge(info.getValue()),
                meta: {
                    width: 185,
                    pickerLabel: "Aktivierung",
                    filterVariant: "select",
                    selectOptions: B2B_SHOP_ACTIVE_FILTER_OPTIONS,
                    help: <>
                        <div><strong>Aktiviert:</strong> im B2B-Shop vorhanden und freigeschaltet, regulär bestellbar.</div>
                        <div><strong>Deaktiviert:</strong> im Shop abgeschaltet und im Katalog nicht bestellbar. Über den CSV-Import ist der Artikel aber weiterhin bestellbar.</div>
                        <div><strong>Durch XXX deaktiviert:</strong> Der Artikelnummer wurde im Shop ein <code>XXX_</code> vorangestellt. Dadurch findet auch der CSV-Import den Artikel nicht mehr; er ist auf allen Wegen gesperrt.</div>
                        <div><strong>–:</strong> im B2B-Shop nicht gefunden, auch nicht unter dem Präfix.</div>
                        <div className="mt-1">Die Nachbarspalte beschreibt dagegen ausschließlich die Lagerverfügbarkeit.</div>
                    </>,
                },
            }),
            columnHelper.accessor("b2b_status", {
                id: "b2b_status",
                header: "B2B-Shop-Lagerstatus",
                cell: (info) => (
                    <span className="d-inline-flex align-items-center">
                        {renderShopStatusBadge(info.getValue())}
                        <StatusTimestampControl
                            timestamp={info.row.original.b2b_status_synced_at}
                            label="Stand des B2B-Shops"
                            artikelnummer={info.row.original.artikelnummer}
                            onRefreshed={handleArticleRefreshed}
                        />
                    </span>
                ),
                meta: {
                    width: 160,
                    pickerLabel: "Verfügbarkeit",
                    filterVariant: "select",
                    selectOptions: SHOP_STATUS_FILTER_OPTIONS,
                    help: "So ist der Artikel aktuell im B2B-Onlineshop (b2b.molotow.com) eingestellt: Auf Lager oder In Zulauf. „–“ = im B2B-Shop nicht gefunden. Das beschreibt die Lagerverfügbarkeit, nicht die Freischaltung in der Nachbarspalte.",
                },
            }),
            columnHelper.accessor("soll_status", {
                id: "soll_status",
                header: "Sollstatus B2B-Shop",
                cell: (info) => {
                    const value = info.getValue();
                    const badge = !value ? (
                        <span className="text-muted">–</span>
                    ) : (
                        (() => {
                            const cfg = SOLL_STATUS_CONFIG[value] || {label: value, variant: "light"};
                            return <span className={`badge bg-${cfg.variant}`}>{cfg.label}</span>;
                        })()
                    );
                    return (
                        <span className="d-inline-flex align-items-center">
                            {badge}
                            <StatusTimestampControl
                                timestamp={info.row.original.synced_at}
                                label="Stand der Weclapp-Bestände"
                                artikelnummer={info.row.original.artikelnummer}
                                onRefreshed={handleArticleRefreshed}
                            />
                        </span>
                    );
                },
                meta: {
                    width: 150,
                    pickerLabel: "Sollstatus B2B-Shop",
                    filterVariant: "select",
                    selectOptions: [
                        {value: "", label: "Alle"},
                        {value: "in", label: "Auf Lager"},
                        {value: "out", label: "In Zulauf"},
                        {value: "no_stock", label: "ohne Bestand"},
                        {value: "__none__", label: "– (nicht überwacht)"},
                    ],
                    help: "Der von der Automatik berechnete Zielzustand für den B2B-Shop nach der Regel „SNV-Bestand < Schwelle (24 Stück) ⇒ In Zulauf, sonst Auf Lager\". Nur für überwachte Artikel gefüllt. Zeigt, wie der B2B-Shop stehen SOLLTE – kann kurzzeitig vom tatsächlichen B2B-Status abweichen, bis der nächste stündliche Abgleich läuft. „–\" = nicht überwacht oder kein SNV-Bestand.",
                },
            }),
            columnHelper.accessor("reserved_quantity", {
                id: "reserved_quantity",
                header: "Reserviert",
                cell: (info) => {
                    const value = info.getValue();
                    return <span className="d-inline-flex align-items-center justify-content-end gap-1 w-100" title={info.row.original.reserved_synced_at ? `Stand der Reservierungen: ${formatTimestamp(info.row.original.reserved_synced_at)}` : "Reservierungen wurden noch nicht abgeglichen"}>
                        {formatQuantity(value)}
                        {Number(value) > 0 && (
                            <HelpTooltip
                                id={`reservation-details-${info.row.original.artikelnummer}`}
                                placement="bottom"
                                size={0.85}
                                iconPath={mdiInformationOutline}
                                ariaLabel={`Reservierungen für ${info.row.original.artikelnummer} anzeigen`}
                                className="reservation-info-trigger"
                                onClick={() => setReservationArtikelnummer(info.row.original.artikelnummer)}
                            >
                                Reservierungen anzeigen
                            </HelpTooltip>
                        )}
                    </span>;
                },
                meta: {
                    width: 120,
                    pickerLabel: "Reserviert",
                    filterVariant: "number",
                    help: "Weclapp führt selbst keine reservierte Menge. Diese Zahl wird hier berechnet: Summe der noch nicht ausgelieferten Mengen aus Aufträgen in Bearbeitung und mit gedruckter Auftragsbestätigung. Je Position zählt bestellte minus bereits gelieferte Menge. Nicht berücksichtigt werden abgeschlossene, manuell geschlossene und stornierte Aufträge. Stand: letzte Reservierungsaktualisierung der jeweiligen Zeile.",
                },
            }),
            ...(priceColumns || []).map((price) => columnHelper.accessor(price.id, {
                id: price.id,
                header: price.label,
                cell: (info) => {
                    const row = info.row.original;
                    const pairs = {
                        price_weclapp_gross1: [row.price_weclapp_gross1, row.price_b2c_price], price_b2c_price: [row.price_weclapp_gross1, row.price_b2c_price],
                        price_weclapp_net4: [row.price_weclapp_net4, row.price_b2b_price], price_b2b_price: [row.price_weclapp_net4, row.price_b2b_price],
                        price_weclapp_net3: [row.price_weclapp_net3, row.price_b2b_molotow_palettprice2], price_b2b_molotow_palettprice2: [row.price_weclapp_net3, row.price_b2b_molotow_palettprice2],
                    };
                    const b2cIds = ["price_weclapp_gross1", "price_b2c_price"];
                    const b2bIds = ["price_weclapp_net4", "price_b2b_price", "price_weclapp_net3", "price_b2b_molotow_palettprice2"];
                    const pair = pairs[price.id];
                    const applies = (priceComparison === "b2c" && b2cIds.includes(price.id)) || (priceComparison === "b2b" && b2bIds.includes(price.id));
                    const [left, right] = pair || [];
                    const bothEmpty = left == null && right == null;
                    const paletteEmpty = priceComparison === "b2b" && ["price_weclapp_net3", "price_b2b_molotow_palettprice2"].includes(price.id) && right == null;
                    const matching = left != null && right != null && Number(left).toFixed(4) === Number(right).toFixed(4);
                    const css = applies && !bothEmpty && !paletteEmpty ? (matching ? "bg-success-subtle" : "bg-danger-subtle") : "";
                    return <span className={`d-block px-1 rounded ${css}`}>{formatPrice(info.getValue(), row[`${price.id}_currency`])}</span>;
                },
                meta: {width: 180, pickerLabel: price.label, filterVariant: "number", help: `${price.label} (${price.source === "weclapp" ? "Weclapp-Verkaufspreis" : price.source === "b2b" ? "B2B-Shop" : "B2C-Shop"}). Fehlende Preise werden als Strich dargestellt.`},
            })),
            columnHelper.accessor("synced_at", {
                id: "synced_at",
                header: "Stand",
                cell: (info) => formatTimestamp(info.getValue()),
                meta: {
                    width: 155,
                    pickerLabel: "Letzter Bestandsabgleich",
                    help: "Zeitpunkt des letzten Bestands-Abgleichs mit Weclapp (Snapshot).",
                },
            }),
        ],
        [warengruppen, warehouseLabels, handleArticleRefreshed, priceColumns, priceComparison]
    );

    const columnPickerGroups = useMemo(() => [
        {
            label: "Artikel",
            columnIds: ["warengruppe"],
        },
        {
            label: "Lager & Reservierung",
            columnIds: ["snv", "snv_zero_since", "lahr", "lahr_zero_since", "ueberwacht", "reserved_quantity", "synced_at"],
        },
        {
            label: "B2C-Shop",
            columnIds: [
                "b2c_active",
                "b2c_status",
                ...priceColumns.filter((price) => price.source === "b2c").map((price) => price.id),
            ],
        },
        {
            label: "B2B-Shop",
            columnIds: [
                "b2b_active",
                "b2b_status",
                "soll_status",
                ...priceColumns.filter((price) => price.source === "b2b").map((price) => price.id),
            ],
        },
        {
            label: "Weclapp-Preise",
            columnIds: priceColumns.filter((price) => price.source === "weclapp").map((price) => price.id),
        },
    ], [priceColumns]);

    const allColumnIds = useMemo(() => columns.map((column) => column.id), [columns]);
    const expandColumns = useCallback((savedColumns) => {
        const saved = Array.isArray(savedColumns) ? savedColumns : [];
        if (saved.includes("__all__")) return allColumnIds;
        const prices = priceColumns.map((price) => price.id);
        const sourcePrices = (source) => priceColumns.filter((price) => price.source === source).map((price) => price.id);
        return [...new Set(saved.flatMap((column) => {
            if (column === "__all_prices__") return prices;
            if (column === "__weclapp_prices__") return sourcePrices("weclapp");
            if (column === "__b2b_prices__") return sourcePrices("b2b");
            if (column === "__b2c_prices__") return sourcePrices("b2c");
            return [column];
        }))].filter((column) => allColumnIds.includes(column));
    }, [allColumnIds, priceColumns]);

    const applyView = useCallback((view, {updateUrl = true, preserveUrlFilters = false} = {}) => {
        if (!view) return;
        const filters = view.filters || {};
        const urlFilters = preserveUrlFilters ? parseInitialFilters() : [];
        // Ein expliziter URL-Filter gewinnt beim Einstieg über einen Link
        // gegenüber einem gleichnamigen Filter aus der gespeicherten Ansicht.
        const viewFilters = (filters.columnFilters || []).filter((filter) => !urlFilters.some((urlFilter) => urlFilter.id === filter.id));
        gridRef.current?.applyState({
            columns: expandColumns(view.columns),
            // Auch alte Ansichten verwenden Platzhalter wie
            // `__all_prices__`. Die Reihenfolge muss sie genau wie die
            // Sichtbarkeit expandieren, sonst wären Preis-Spalten nach einem
            // Reload willkürlich ans Tabellenende gerutscht.
            columnOrder: expandColumns(filters.columnOrder || view.columns),
            filters: [...viewFilters, ...urlFilters],
            sorting: filters.sorting || [{id: "artikelnummer", desc: false}],
            // Alte Ansichten besitzen noch keinen Zusatzfilter-Snapshot.
            // Der sichtbare Standard bleibt trotzdem der globale Default,
            // statt einen Wert aus der zuvor geöffneten Ansicht mitzunehmen.
            additionalFilters: {...DEFAULT_ADDITIONAL_FILTERS, ...(filters.additionalFilters || {})},
            search: filters.search || "",
            advancedSearch: filters.advancedSearch || {version: 1, article_numbers: "", groups: []},
        });
        setActiveViewId(view.id);
        if (updateUrl && typeof window !== "undefined") {
            const url = new URL(window.location.href);
            // Ein bewusster Ansichtswechsel ist ein neuer Zustand. Die beiden
            // Link-Vorfilter gelten nur beim Einstieg und dürfen beim Reload
            // nicht wieder über die gerade gewählte Ansicht gelegt werden.
            ["ueberwacht", "soll_status"].forEach((key) => url.searchParams.delete(key));
            url.searchParams.set("view", view.slug);
            window.history.replaceState({}, "", url);
        }
    }, [expandColumns]);

    useEffect(() => {
        const initial = views.find((view) => view.id === activeViewId);
        if (initial && columns.length) applyView(initial, {updateUrl: false, preserveUrlFilters: true});
        // Nur beim Eintreffen der vorgegebenen Ansichten anwenden; spätere
        // Benutzeränderungen bleiben ausdrücklich persönlich.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [columns.length]);

    const onGridStateChange = useCallback((next) => {
        setGridState((current) => JSON.stringify(current) === JSON.stringify(next) ? current : next);
    }, []);

    const activeView = views.find((view) => view.id === activeViewId);
    const priceComparisonMode = activeView?.slug === "b2c-preise"
        ? "b2c"
        : activeView?.slug === "b2b-preise"
            ? "b2b"
            : null;
    const gridDiffersFromActiveView = useMemo(() => {
        if (!activeView || !gridState) return false;
        const defaults = activeView.filters || {};
        const equal = (left, right) => JSON.stringify(left || []) === JSON.stringify(right || []);
        return !equal(gridState.filters, defaults.columnFilters)
            || !equal(gridState.sorting, defaults.sorting || [{id: "artikelnummer", desc: false}])
            || (gridState.search || "") !== (defaults.search || "")
            || JSON.stringify(gridState.advancedSearch || {}) !== JSON.stringify(defaults.advancedSearch || {version: 1, article_numbers: "", groups: []})
            || JSON.stringify({...DEFAULT_ADDITIONAL_FILTERS, ...(gridState.additionalFilters || {})}) !== JSON.stringify({...DEFAULT_ADDITIONAL_FILTERS, ...(defaults.additionalFilters || {})});
    }, [activeView, gridState]);

    const togglePriceComparison = () => {
        const state = gridRef.current?.getState();
        if (priceComparison) {
            setPriceComparison(null);
            gridRef.current?.applyState({
                ...state,
                filters: (state?.filters || []).filter((filter) => filter.id !== "price_comparison"),
            });
            return;
        }
        setPriceComparison(priceComparisonMode);
    };

    const toggleOnlyPriceDifferences = (event) => {
        const state = gridRef.current?.getState();
        gridRef.current?.applyState({
            ...state,
            filters: [
                ...(state?.filters || []).filter((filter) => filter.id !== "price_comparison"),
                ...(event.target.checked ? [{id: "price_comparison", value: priceComparison}] : []),
            ],
        });
    };

    const saveView = async ({create = false} = {}) => {
        const target = create ? null : activeView;
        const name = target ? target.name : viewName.trim();
        if (!name || !gridState) return;
        const payload = {name, columns: gridState.columns, filters: {columnFilters: gridState.filters, columnOrder: gridState.columnOrder, sorting: gridState.sorting, additionalFilters: gridState.additionalFilters, search: gridState.search, advancedSearch: gridState.advancedSearch}};
        const response = target ? await axios.put(`/stock/overview/views/${target.id}`, payload) : await axios.post("/stock/overview/views", payload);
        const saved = response.data.view;
        setViews((current) => target ? current.map((item) => item.id === saved.id ? saved : item) : [...current, saved]);
        if (create) setDraftViewIds((current) => [...current, saved.id]);
        applyView(saved);
        setViewName("");
    };

    const startEditing = () => {
        setDraftViewIds(views.map((view) => view.id));
        setEditMode(true);
    };

    const moveView = (sourceId, targetId, afterTarget) => {
        setDraftViewIds((current) => {
            if (sourceId === targetId || !current.includes(sourceId) || !current.includes(targetId)) return current;
            const next = current.filter((id) => id !== sourceId);
            const targetIndex = next.indexOf(targetId);
            next.splice(targetIndex + (afterTarget ? 1 : 0), 0, sourceId);
            return next;
        });
    };

    const saveEditing = async () => {
        const response = await axios.put("/stock/overview/views-reorder", {ids: draftViewIds});
        setViews(response.data.views);
        setEditMode(false);
    };

    const cancelEditing = () => {
        setEditMode(false);
        // Header-Reihenfolge, Filter und Sichtbarkeit können während der
        // Bearbeitung bereits lokal geändert sein. Abbrechen bedeutet daher
        // nicht nur „Button-Entwurf verwerfen“, sondern den gespeicherten
        // Zustand der aktiven Ansicht vollständig wiederherstellen.
        applyView(activeView, {updateUrl: false});
    };

    const displayedViews = editMode
        ? draftViewIds.map((id) => views.find((view) => view.id === id)).filter(Boolean)
        : views;

    const additionalFilters = useMemo(
        () => [
            {
                key: "only_shop_articles",
                label: "Nur Artikel zeigen, die in mindestens einem Onlineshop verkauft werden",
                defaultValue: true,
                help: "Blendet Artikel aus, die es weder im B2C- noch im B2B-Shop gibt (z. B. reine Weclapp-Stammdaten ohne Shop-Anbindung). Ausschalten zeigt wirklich alle Artikel dieser Lagerübersicht.",
            },
            {key: "shop_scope", label: "Shop", type: "select", options: [{value: "", label: "Alle Shop-Artikel"}, {value: "b2c", label: "Nur B2C-Artikel anzeigen"}, {value: "b2b", label: "Nur B2B-Artikel anzeigen"}], defaultValue: "", help: "Begrenzt die Übersicht auf Artikel, die tatsächlich im ausgewählten Shop existieren."},
        ],
        []
    );

    return (
        <Container fluid className="pt-2">
            {stockWarning?.stale && (
                <Alert variant="warning">
                    <strong>Bestandsdaten möglicherweise veraltet</strong> – letzter erfolgreicher Abgleich: {formatTimestamp(stockWarning.last_successful_at)}.
                    <Button size="sm" variant="outline-dark" className="ms-2" disabled={refreshingStock} onClick={refreshStockData}>{refreshingStock ? "Aktualisiere…" : "Jetzt aktualisieren"}</Button>
                    {stockRefreshError && <span className="ms-2 text-danger">{stockRefreshError}</span>}
                </Alert>
            )}
            <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                <div className="d-inline-flex align-items-stretch p-1 rounded-pill bg-body-secondary" role="tablist" aria-label="Lageransichten">
                    {displayedViews.map((view) => <Button key={view.id} size="sm" variant="link" role="tab" aria-selected={view.id === activeViewId} draggable={editMode} onDragStart={(event) => { viewWasDraggedRef.current = true; event.dataTransfer.effectAllowed = "move"; event.dataTransfer.setData("application/x-stock-view", String(view.id)); }} onDragEnd={() => window.setTimeout(() => { viewWasDraggedRef.current = false; }, 0)} onDragOver={(event) => editMode && event.preventDefault()} onDrop={(event) => { if (!editMode) return; event.preventDefault(); const bounds = event.currentTarget.getBoundingClientRect(); moveView(Number(event.dataTransfer.getData("application/x-stock-view")), view.id, event.clientX > bounds.left + (bounds.width / 2)); }} className={`rounded-pill border-0 text-decoration-none ${view.id === activeViewId ? "bg-white text-body shadow-sm fw-semibold" : "text-body"} ${editMode ? "cursor-grab" : ""}`} style={{padding: ".3rem 1.2rem"}} onClick={() => { if (!viewWasDraggedRef.current) { setPriceComparison(null); applyView(view); } }}>{view.name}</Button>)}
                </div>
                <HelpTooltip placement="right" size={0.85}>Bestands-, Preis- und Zustandsinformationen aus Weclapp und den Shops.</HelpTooltip>
                {isAdmin && <>
                    {!editMode && <Button size="sm" variant="outline-secondary" onClick={startEditing}>Bearbeitungsmodus</Button>}
                    {editMode && <>
                        <span className="small text-muted">Ansichten und Spaltenüberschriften ziehen, anschließend sichern.</span>
                        <Button size="sm" variant="primary" disabled={!activeView || !gridState} onClick={() => saveView()}>Aktuelle Ansicht sichern</Button>
                        <Form.Control size="sm" value={viewName} onChange={(event) => setViewName(event.target.value)} placeholder="Neue Ansicht" style={{width: 160}} />
                        <Button size="sm" variant="success" onClick={saveEditing}>Reihenfolge speichern</Button>
                        <Button size="sm" variant="outline-secondary" onClick={cancelEditing}>Abbrechen</Button>
                    </>}
                </>}
            </div>

            <DataGrid
                ref={gridRef}
                columns={columns}
                getRowId={(row) => row.artikelnummer}
                initialColumnFilters={initialColumnFilters}
                initialSorting={[{id: "artikelnummer", desc: false}]}
                lockedColumnIds={["hex_color", "artikelnummer", "bezeichnung"]}
                columnPickerGroups={columnPickerGroups}
                columnReorderMode={isAdmin && editMode}
                onStateChange={onGridStateChange}
                groupingColumnId="warengruppe"
                emptyLabel="Keine Artikel gefunden."
                dataUrl="/stock/overview/data"
                groupsUrl="/stock/overview/groups"
                exportUrl="/stock/overview/export"
                additionalFilters={additionalFilters}
                advancedSearchConfig={{fields: advancedSearchFields, savedSearches, saveUrl: "/stock/overview/saved-searches"}}
                defaultPageSize={100}
                compactHeader
                toolbarActions={priceComparisonMode && <>
                    <div className="d-inline-flex align-items-center gap-1">
                        <Form.Check
                            type="switch"
                            id="stock-price-comparison-switch"
                            className="mb-0"
                            label="Preisvergleich starten"
                            checked={!!priceComparison}
                            onChange={togglePriceComparison}
                        />
                        <HelpTooltip id="stock-price-comparison-help" placement="bottom">
                            {priceComparisonMode === "b2c"
                                ? "Vergleicht PG1 aus Weclapp mit dem B2C-Shoppreis."
                                : "Vergleicht PG2 mit dem B2B-Shoppreis sowie PG3 mit dem Palettenpreis, falls dieser im Shop vorhanden ist."}
                            {" "}Grün bedeutet gleich, rot abweichend; „Nur Abweichungen“ zeigt ausschließlich abweichende Artikel.
                        </HelpTooltip>
                    </div>
                    {priceComparison && (
                        <Form.Check
                            type="switch"
                            id="stock-price-comparison-only-differences"
                            className="mb-0"
                            label="Nur Abweichungen"
                            checked={(gridState?.filters || []).some((filter) => filter.id === "price_comparison")}
                            onChange={toggleOnlyPriceDifferences}
                        />
                    )}
                </>}
                resetAction={gridDiffersFromActiveView && <button type="button" className="btn btn-link btn-sm p-0" onClick={() => { setPriceComparison(null); applyView(activeView, {updateUrl: false}); }}>Filter und Sortierung zurücksetzen</button>}
            />

            <ArticleHistoryModal
                show={!!historyArtikelnummer}
                artikelnummer={historyArtikelnummer}
                onHide={() => setHistoryArtikelnummer(null)}
            />
            <ReservationModal show={!!reservationArtikelnummer} artikelnummer={reservationArtikelnummer} onHide={() => setReservationArtikelnummer(null)} weclappBaseUrl={weclappBaseUrl} />
        </Container>
    );
}
